/**
 * Générateur — Automatisme : écrire une fraction sous la forme d'une somme
 * d'un nombre entier et d'une fraction inférieure à 1 : 17/5 = 3 + 2/5.
 * Attendu ID : 1053 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8). Cadrage rédigé
 * par le sous-agent programme-maths (épreuve de Musk incluse), validé par
 * le prof le 2026-09-08.
 *
 * Le geste : devant une fraction plus grande que 1 dont le dénominateur est
 * dans les tables, écrire d'un seul regard l'entier qu'elle contient (le
 * multiple du dénominateur juste en dessous du numérateur) et la fraction
 * plus petite que 1 qui reste, sur le même dénominateur. UN seul sens,
 * fraction → somme (le sens inverse est l'automatisme suivant, addition de
 * fractions). Jamais d'encadrement (CM, gen_628), jamais de droite (1050,
 * 623), jamais « quotient / reste » dans une consigne (1040).
 *
 * Plan de cartes validé, deux cartes :
 *   - Série flash                     (4 lignes a/b = ⬚ + ⬚/b, saisie)
 *   - L'entier et ce qui reste        (QCM 4 choix, Vrai/Faux 3 égalités ;
 *                                      le Relier a été supprimé à la revue,
 *                                      redite du QCM et de la Série flash)
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Triplet (q, r, b) : b de 2 à 10 (les tables), q de 1 à 9, r de 1 à b − 1
 * PREMIER avec b (une seule écriture juste, les fractions égales sont 1051),
 * a = q × b + r ≤ 99. Jamais r = 0 (fraction égale à un entier : 1048).
 * Série flash : quatre dénominateurs distincts, les trois zones d'entier
 * (q = 1 ; 2 à 5 ; 6 à 9), un cas DISCRIMINANT (les deux chiffres du
 * numérateur lus séparément donnent une fraction plausible : 23/4 →
 * « 2 + 3/4 »), un cas r = b − 1 (la tentation de l'entier suivant).
 * SACRIFIÉ : 17/5 (l'exemple du programme et du coup de pouce) sous toutes
 * ses formes, fraction, somme 3 + 2/5, affirmation, leurre ; auto-testé.
 *
 * ── Distracteurs (familles nommées, cohérentes entre les cartes) ─────────
 * Pour a/b = q + r/b, a = « d1 d2 » :
 *   lu_tel_quel            : d1 + d2/b, les chiffres du numérateur lus
 *                            séparément — L'ERREUR PRINCIPALE (au QCM dès
 *                            qu'elle est PLAUSIBLE, d2 de 1 à b − 1 ; sinon
 *                            elle se dénonce à sa forme et n'est pas tirée) ;
 *   entier_reste_echanges  : r + q/b, l'entier et le reste à la mauvaise
 *                            place ;
 *   reste_nu               : q + r, le reste sans son dénominateur ;
 *   decalage               : (q ± 1) + r/b, le multiple voisin ;
 *   entiers_pas_tous_sortis: (q − 1) + (r + b)/b, MÊME VALEUR que la clé :
 *                            jamais au QCM ni au Vrai/Faux ; en saisie la
 *                            consigne (« plus petite que 1 ») la rend fausse.
 * Chaque distracteur est vérifié différent de la clé et des autres EN
 * VALEUR (rationnel exact) ; collision → nouveau tirage.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1053;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_ENTIER = 'L\'entier et ce qui reste';

// ─── Le domaine ───────────────────────────────────────────────────────────

const B_MIN = 2;
const B_MAX = 10;
const Q_MIN = 1;
const Q_MAX = 9;
const A_MAX = 99;

/** L'exemple du programme et du coup de pouce, sacrifié : 17/5 = 3 + 2/5. */
export const SACRIFIE = { q: 3, r: 2, b: 5, a: 17 };

const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));

/** Le sacrifice est exclu ici même : aucun triplet (3, 2, 5) ne peut être construit. */
const valide = (q, r, b) => b >= B_MIN && b <= B_MAX && q >= Q_MIN && q <= Q_MAX && r >= 1 && r < b && gcd(r, b) === 1
    && q * b + r <= A_MAX && !(q === SACRIFIE.q && r === SACRIFIE.r && b === SACRIFIE.b);

const triplet = (q, r, b) => {
    if (!valide(q, r, b)) throw new Error(`gen_1053 : triplet hors domaine ${q} + ${r}/${b}`);
    return { q, r, b, a: q * b + r };
};

/** Chiffres du numérateur : d1 (dizaines) et d2 (unités) ; null si a < 10. */
const chiffres = (t) => (t.a >= 10 ? { d1: Math.floor(t.a / 10), d2: t.a % 10 } : null);
/** Cas discriminant : « d1 + d2/b » est une somme plausible (d2 de 1 à b − 1) et diffère de la clé. */
const estDiscriminant = (t) => {
    const c = chiffres(t);
    return Boolean(c) && c.d2 >= 1 && c.d2 < t.b && !(c.d1 === t.q && c.d2 === t.r);
};

/**
 * Un triplet au hasard. `q` ou `qMin/qMax` fixent la zone d'entier ; `b`
 * fixe le dénominateur ; `discriminant`, `resteMax` imposent les cas.
 */
const tirerTriplet = ({ q = null, qMin = Q_MIN, qMax = Q_MAX, b = null, discriminant = null, resteMax = null, exclusB = [] } = {}) => {
    for (let essai = 0; essai < 300; essai++) {
        const bb = b ?? randInt(B_MIN, B_MAX);
        const qq = q ?? randInt(qMin, qMax);
        const rr = randInt(1, bb - 1);
        if (exclusB.includes(bb) || !valide(qq, rr, bb)) continue;
        const t = triplet(qq, rr, bb);
        if (discriminant === true && !estDiscriminant(t)) continue;
        if (discriminant === false && estDiscriminant(t)) continue;
        if (resteMax === true && rr !== bb - 1) continue;
        return t;
    }
    return null;
};

// ─── Écritures ────────────────────────────────────────────────────────────

const fracTex = (n, d) => `\\dfrac{${n}}{${d}}`;
const sommeTex = (q, n, d) => `${q} + ${fracTex(n, d)}`;
/** Valeur exacte d'une somme q + n/d, en (num, den). */
const valeur = (q, n, d) => ({ num: q * d + n, den: d });
const memeValeur = (u, v) => u.num * v.den === v.num * u.den;

const EXPLICATIONS = {
    lu_tel_quel: 'Les chiffres du numérateur ne se lisent pas séparément : je cherche le multiple du dénominateur juste en dessous.',
    entier_reste_echanges: 'L\'entier, c\'est le nombre de fois où le dénominateur tient dans le numérateur ; ce qui reste va en haut de la fraction.',
    reste_nu: 'Ce qui reste, ce sont des parts : il faut les écrire sur le dénominateur.',
    decalage: 'Je prends le multiple du dénominateur juste en dessous du numérateur, ni celui d\'avant, ni celui d\'après.',
    entiers_pas_tous_sortis: 'La fraction qui reste doit être plus petite que 1 : s\'il reste plus de parts qu\'une unité, je sors encore un entier.',
    regle: 'L\'entier, c\'est le multiple du dénominateur juste en dessous du numérateur ; ce qui reste va sur le même dénominateur.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

/** Les candidats pour a/b = q + r/b, chacun avec sa valeur exacte. */
const candidats = (t) => {
    const c = [];
    const ch = chiffres(t);
    // lu_tel_quel seulement s'il est PLAUSIBLE (unités de 1 à b − 1) : « 1 + 3/3 »
    // ou « 1 + 7/5 » se dénoncent à leur forme (épreuve de Musk, revue prof)
    if (estDiscriminant(t)) c.push({ texte: `$${sommeTex(ch.d1, ch.d2, t.b)}$`, code: 'lu_tel_quel', val: valeur(ch.d1, ch.d2, t.b) });
    if (t.q !== t.r && t.q < t.b) c.push({ texte: `$${sommeTex(t.r, t.q, t.b)}$`, code: 'entier_reste_echanges', val: valeur(t.r, t.q, t.b) });
    c.push({ texte: `$${t.q} + ${t.r}$`, code: 'reste_nu', val: valeur(t.q + t.r, 0, 1) });
    const dq = randomChoice([t.q - 1, t.q + 1].filter((x) => x >= 1));
    c.push({ texte: `$${sommeTex(dq, t.r, t.b)}$`, code: 'decalage', val: valeur(dq, t.r, t.b) });
    const cle = valeur(t.q, t.r, t.b);
    // la somme sacrifiée 3 + 2/5 ne sort JAMAIS, même comme distracteur (22/5
    // par décalage, 32/5 lu tel quel, 13/5 par échange : vécu à l'auto-test)
    const sacrifiee = `$${sommeTex(SACRIFIE.q, SACRIFIE.r, SACRIFIE.b)}$`;
    return c.filter((d) => !memeValeur(d.val, cle) && d.texte !== sacrifiee);
};

/**
 * QCM : lu_tel_quel dès qu'il est plausible, puis les autres familles en
 * rotation (échange, reste nu, décalage). null si trop peu de candidats.
 */
const distracteursQcm = (t) => {
    const cands = candidats(t);
    const lu = cands.find((d) => d.code === 'lu_tel_quel');
    const autres = cands.filter((d) => d.code !== 'lu_tel_quel');
    const choix = lu ? [lu] : [];
    for (let k = 0; k < 40 && choix.length < 3; k++) {
        const d = randomChoice(autres);
        if (choix.some((x) => x.code === d.code || memeValeur(x.val, d.val))) continue;
        choix.push(d);
    }
    if (choix.length < 3) return null;
    return choix;
};

// ─── Carte 1 : Série flash (4 lignes, saisie de l'entier et du numérateur) ─

const CONSIGNE_FLASH = '<p><b>Écris chaque fraction comme un entier plus une fraction plus petite que 1</b>, sur le même dénominateur.</p>';

/** « a/b = ⬚ + ⬚/b », l'entier puis le numérateur en trous, étai sur la case du haut. */
const ligneFlash = (t) => `${fracTex(t.a, t.b)} = {{${t.q}}} + \\dfrac{{{${t.r}}}\\rule[-0.55em]{0pt}{0.55em}}{${t.b}}`;

const genFlash = () => {
    for (let essai = 0; essai < 120; essai++) {
        const items = [];
        const zones = shuffle([{ q: 1 }, { qMin: 2, qMax: 5 }, { qMin: 6, qMax: 9 }, {}]);
        let ok = true;
        for (const z of zones) {
            const t = tirerTriplet({ ...z, exclusB: items.map((it) => it.b) });
            if (!t) { ok = false; break; }
            items.push(t);
        }
        if (!ok) continue;
        if (!items.some(estDiscriminant) || !items.some((t) => t.r === t.b - 1)) continue;
        const phrase = [items.slice(0, 2), items.slice(2, 4)].map((paire) => `$${paire.map(ligneFlash).join(' \\quad ; \\quad ')}$`).join('\n');
        const payload = makeTexteTrou(phrase, CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, entiers_pas_tous_sortis: EXPLICATIONS.entiers_pas_tous_sortis } };
        payload._meta.params = { items };
        return payload;
    }
    // Repli déterministe vérifié : 7/2 (q 1), 23/4 (q 5, discriminant), 55/8 (q 6, r = b − 1), 25/3 (q 8).
    const items = [triplet(1, 1, 2), triplet(5, 3, 4), triplet(6, 7, 8), triplet(8, 1, 3)];
    const phrase = [items.slice(0, 2), items.slice(2, 4)].map((paire) => `$${paire.map(ligneFlash).join(' \\quad ; \\quad ')}$`).join('\n');
    const payload = makeTexteTrou(phrase, CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, entiers_pas_tous_sortis: EXPLICATIONS.entiers_pas_tous_sortis } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2, format A : QCM, quatre choix ────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 120; essai++) {
        // numérateur à deux chiffres (l'erreur principale a besoin de deux chiffres), discriminant une fois sur deux
        const t = tirerTriplet({ discriminant: randomChoice([true, false]) });
        if (!t || t.a < 10) continue;
        const faux = distracteursQcm(t);
        if (!faux) continue;
        const bon = `$${sommeTex(t.q, t.r, t.b)}$`;
        const choix = [bon, ...faux.map((f) => f.texte)];
        if (new Set(choix).size !== 4) continue;
        const texte = `<p><b>Comment s'écrit ${tex(fracTex(t.a, t.b))} comme un entier plus une fraction plus petite que 1 ?</b></p>`;
        const payload = makeQcm(choix, bon, texte, opts(L_ENTIER));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...t, discriminant: estDiscriminant(t), distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe vérifié : 23/4 = 5 + 3/4 ; lu_tel_quel 2 + 3/4, decalage 4 + 3/4, reste_nu 5 + 3.
    const t = triplet(5, 3, 4);
    const bon = `$${sommeTex(5, 3, 4)}$`;
    const payload = makeQcm([bon, `$${sommeTex(2, 3, 4)}$`, `$${sommeTex(4, 3, 4)}$`, '$5 + 3$'], bon,
        `<p><b>Comment s'écrit ${tex(fracTex(23, 4))} comme un entier plus une fraction plus petite que 1 ?</b></p>`, opts(L_ENTIER));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...t, discriminant: true, distracteurs: [{ code: 'lu_tel_quel' }, { code: 'decalage' }, { code: 'reste_nu' }] };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 égalités) ────────────────────────

/** « a/b = somme », vraie ou fausse (famille tirée dans les candidats, lu_tel_quel imposable). */
const affirmation = (t, vraie, famille = null) => {
    const gauche = fracTex(t.a, t.b);
    if (vraie) return { ligne: tex(`${gauche} = ${sommeTex(t.q, t.r, t.b)}`), code: null };
    const cands = candidats(t);
    const d = famille ? cands.find((x) => x.code === famille) : randomChoice(cands);
    if (!d) return null;
    return { ligne: tex(`${gauche} = ${d.texte.slice(1, -1)}`), code: d.code };
};

const genVf = () => {
    for (let essai = 0; essai < 120; essai++) {
        const ts = [tirerTriplet(), tirerTriplet(), tirerTriplet()];
        if (ts.some((t) => !t) || new Set(ts.map((t) => `${t.a}/${t.b}`)).size < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        // au moins une fausse lu_tel_quel par tableau : sur la première fausse
        const iLu = verites.indexOf(false);
        const affs = ts.map((t, i) => affirmation(t, verites[i], i === iLu ? 'lu_tel_quel' : null));
        if (affs.some((a) => !a)) continue;
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_ENTIER));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { triplets: ts, verites, distracteurs: affs.filter((a) => a.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe : 23/4 = 5 + 3/4 vrai ; 25/3 = 2 + 5/3 faux (lu_tel_quel) ; 13/2 = 5 + 1/2 faux (decalage).
    const ts = [triplet(5, 3, 4), triplet(8, 1, 3), triplet(6, 1, 2)];
    const lignes = [
        tex(`${fracTex(23, 4)} = ${sommeTex(5, 3, 4)}`),
        tex(`${fracTex(25, 3)} = ${sommeTex(2, 5, 3)}`),
        tex(`${fracTex(13, 2)} = ${sommeTex(5, 1, 2)}`),
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_ENTIER));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { triplets: ts, verites: [true, false, false], distracteurs: [{ code: 'lu_tel_quel' }, { code: 'decalage' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'b de 2 à 10, q de 1 à 9, r de 1 à b − 1 premier avec b, a ≤ 99 ; 17/5 sacrifié sous toutes ses formes',
        'chaque distracteur est vérifié différent de la clé et des autres EN VALEUR ; entiers_pas_tous_sortis (même valeur) jamais en choix',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    entier_qcm_unique: {
        support: '« Comment s\'écrit a/b comme un entier plus une fraction plus petite que 1 ? », a à deux chiffres, discriminant une fois sur deux',
        fixes: { lu_tel_quel: 'd1 + d2/b dès qu\'il est plausible (d2 de 1 à b − 1) : les chiffres du numérateur lus séparément (l\'erreur principale) ; denominateur_perdu (q + r/a) supprimé à l\'épreuve de Musk, il se voyait à sa forme' },
        rotation: {
            entier_reste_echanges: 'r + q/b',
            reste_nu: 'q + r, le reste sans dénominateur',
            decalage: '(q ± 1) + r/b',
        },
    },
    entier_tableau_vf: 'trois égalités a/b = somme, 1 ou 2 vraies, une fausse lu_tel_quel au moins par tableau',
    entier_relier: 'SUPPRIMÉ à la revue (épreuve de Musk) : redite du QCM (reconnaître) et de la Série flash (quatre fois) ; il portait aussi un déséquilibre de dénominateurs à réparer',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    entier_qcm_unique: genQcm,
    entier_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'entier', label: L_ENTIER, formatsList: [
        { key: 'entier_qcm_unique', label: 'QCM' },
        { key: 'entier_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
